/**
 * services/BetaApplicationStatusHistoryService.ts
 * Status history tracking service for beta applications
 *
 * Architecture: WU1.5 Task 4 - Pure business logic service
 *
 * Handles tracking of all status changes for beta applications, providing
 * complete audit trail of who changed what and when for compliance and reporting.
 *
 * NOTE: This service implements pure business logic patterns.
 * All data access is delegated to betaApplicationStatusHistoryRepository.
 */

import type {
  BetaApplicationStatusHistory,
  BetaApplicationStatus
} from '@/types/supabase';
import {
  betaApplicationStatusHistoryRepository,
  type StatusHistoryWithApplication,
  type StatusChangeStats
} from '@/repositories/betaApplicationStatusHistoryRepository';

// Re-export types for backward compatibility
export type { StatusHistoryWithApplication, StatusChangeStats };

/**
 * Beta Application Status History Service
 *
 * Provides business logic for status history tracking.
 * Delegates all data access to betaApplicationStatusHistoryRepository.
 *
 * APPEND-ONLY PATTERN: Status history is audit data.
 * No update() or delete() methods to preserve audit trail integrity.
 */
export class BetaApplicationStatusHistoryService {
  constructor() {
    // Pure business logic only - no database access
    // TODO: [CONTRACTOR] Implement audit trail analytics integration - track admin decision patterns, timing analysis, and compliance reporting for business intelligence
  }

  // ===== BASIC CRUD WRAPPER METHODS =====

  /**
   * Get a single status history record by ID
   *
   * @param id - Status history record ID
   * @returns Promise resolving to history record or null
   */
  async getById(id: string): Promise<BetaApplicationStatusHistory | null> {
    return await betaApplicationStatusHistoryRepository.getById(id);
  }

  /**
   * Get all status history records
   *
   * @returns Promise resolving to array of all history records
   */
  async getAll(): Promise<BetaApplicationStatusHistory[]> {
    const { items } = await betaApplicationStatusHistoryRepository.list();
    return items;
  }

  // ===== STATUS CHANGE TRACKING METHODS =====

  /**
   * Record a status change for a beta application
   *
   * @param applicationId - Beta application ID
   * @param previousStatus - Previous status (null for initial status)
   * @param newStatus - New status
   * @param changedBy - Admin who made the change
   * @param adminNotes - Optional notes about the change
   * @returns Promise resolving to status history record
   */
  async recordStatusChange(
    applicationId: string,
    previousStatus: BetaApplicationStatus | null,
    newStatus: BetaApplicationStatus,
    changedBy: string,
    adminNotes?: string
  ): Promise<BetaApplicationStatusHistory> {
    const historyData = {
      applicationId,
      previousStatus,
      newStatus,
      changedBy,
      adminNotes: adminNotes || null,
      changedAt: new Date().toISOString()
    };

    return await betaApplicationStatusHistoryRepository.create(historyData);
  }

  // ===== HISTORY QUERY METHODS =====

  /**
   * Get status history for a specific application
   *
   * @param applicationId - Beta application ID
   * @returns Promise resolving to array of status history records
   */
  async getByApplication(
    applicationId: string
  ): Promise<BetaApplicationStatusHistory[]> {
    return await betaApplicationStatusHistoryRepository.getByApplicationId(
      applicationId
    );
  }

  /**
   * Get status history with application details
   *
   * Delegates to repository for data access. Service layer provides
   * business logic orchestration only.
   *
   * @param applicationId - Beta application ID
   * @returns Promise resolving to status history with application info (camelCase)
   */
  async getByApplicationWithDetails(
    applicationId: string
  ): Promise<StatusHistoryWithApplication[]> {
    return await betaApplicationStatusHistoryRepository.listByApplicationId(
      applicationId
    );
  }

  /**
   * Get recent status changes across all applications
   *
   * Delegates to repository for data access. Service layer provides
   * business logic orchestration only.
   *
   * @param limit - Maximum number of records to return
   * @param adminFilter - Optional filter by admin username
   * @returns Promise resolving to recent status changes (camelCase)
   */
  async getRecentChanges(
    limit: number = 50,
    adminFilter?: string
  ): Promise<StatusHistoryWithApplication[]> {
    return await betaApplicationStatusHistoryRepository.listRecent(
      limit,
      adminFilter
    );
  }

  // ===== REPORTING METHODS =====

  /**
   * Get status change summary for all applications
   *
   * @returns Promise resolving to array of status summaries
   */
  async getStatusSummaries(): Promise<
    Array<{
      applicationId: string;
      applicantEmail: string;
      totalChanges: number;
      currentStatus: BetaApplicationStatus;
      submittedAt: string;
      lastChangedAt: string;
      lastChangedBy: string;
    }>
  > {
    const summaries = await betaApplicationStatusHistoryRepository.listSummaries();

    return summaries.map((s) => ({
      applicationId: s.applicationId,
      applicantEmail: s.applicantEmail,
      totalChanges: s.totalChanges,
      currentStatus: s.currentStatus as BetaApplicationStatus,
      submittedAt: s.submittedAt,
      lastChangedAt: s.lastChangedAt,
      lastChangedBy: s.lastChangedBy
    }));
  }

  /**
   * Get status change statistics
   *
   * @param dateRange - Optional date range filter
   * @returns Promise resolving to status change stats
   */
  async getStatusChangeStats(dateRange?: {
    start: string;
    end: string;
  }): Promise<{
    totalChanges: number;
    changesByStatus: Record<string, number>;
    changesByAdmin: Record<string, number>;
    changesByDate: Record<string, number>;
  }> {
    return await betaApplicationStatusHistoryRepository.getStats(dateRange);

    // TODO: [CONTRACTOR] Implement compliance and audit features - data retention policies, export capabilities, and regulatory compliance reporting for audit trails
  }

  // ===== UTILITY METHODS =====

  /**
   * Get the most recent status change for an application
   *
   * @param applicationId - Beta application ID
   * @returns Promise resolving to most recent status change or null
   */
  async getMostRecentChange(
    applicationId: string
  ): Promise<BetaApplicationStatusHistory | null> {
    return await betaApplicationStatusHistoryRepository.getMostRecent(
      applicationId
    );
  }

  /**
   * Count total status changes for an application
   *
   * @param applicationId - Beta application ID
   * @returns Promise resolving to change count
   */
  async getChangeCount(applicationId: string): Promise<number> {
    return await betaApplicationStatusHistoryRepository.getCount(applicationId);
  }
}

// Export singleton instance
export const betaApplicationStatusHistoryService =
  new BetaApplicationStatusHistoryService();
